import { WidgetProps } from '@features/dashboard/types';
import darkTheme from '@shared/themes/darkTheme';
import { ColDef, GetRowIdFunc, GetRowIdParams, IRowNode, ValueFormatterFunc } from 'ag-grid-enterprise';
import { Grid } from '../grid';
import Settings from '../settings/Settings';
import Widget, { Header } from '../Widget';
import { useWatchlistStream } from './hooks/WatchlistPriceStream';

import { ProductEntry } from '@features/base/private/components/product-entry/ProductEntry';
import { WidgetType } from '@features/dashboard/enums';
import { Product, ProductTenor } from '@protos/product';
import { useDashboardContext } from '@shared/contexts/DashboardContext';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  CurrentPriceCellComponent,
  Headers,
  ReferencePriceComponent,
  RowButtonCellComponent,
  TargetPriceCellComponent,
  WatchRowButtonComponentParams,
} from './WatchlistComponents';
import { WatchlistFooter } from './WatchlistFooter';
import { WatchlistEntry, WatchlistEntryOptions, WatchlistPayload } from './WatchlistTypes';

export type WatchlistProps = WidgetProps & { suppressGridVirtualisation?: boolean };

const priceFormatter: ValueFormatterFunc<number, number> = params => {
  return params.value?.toFixed(2) || '-';
};

const stringFormatter: ValueFormatterFunc<string, string> = params => {
  return params.value?.toUpperCase() || '-';
};

export const Watchlist = (props: WatchlistProps) => {
  const { payload, id, ...otherProps } = props;

  const { addNewWidgets } = useDashboardContext();

  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);

  const { editWidgetPayloadById } = useDashboardContext();

  const addTradeWidgetCallback = (entry: WatchlistEntry) => {
    const id = uuidv4();

    addNewWidgets([
      {
        id,
        type: WidgetType.TradingV2,
        payload: {
          selectedProduct: entry.symbol,
          selectedTenor: entry.tenorCode,
        },
      },
    ]);
  };

  const { entries, addNewWatch, removeWatch, updateWatch, onGridReady } = useWatchlistStream(
    editWidgetPayloadById,
    id,
    addTradeWidgetCallback,
    (payload as WatchlistPayload).records
  );

  const defaultColDef: ColDef = {
    suppressMovable: true,
    cellStyle: { padding: '4px', fontSize: '1.1em' },
    minWidth: 100,
    flex: 1,
  };

  const getRowId: GetRowIdFunc<WatchlistEntry> = (params: GetRowIdParams<WatchlistEntry>) => {
    return String(params.data.id);
  };

  const onCancel = (data: WatchlistEntry | undefined): boolean => {
    if (data && removeWatch(data.id)) {
      return true;
    } else {
      return false;
    }
  };

  const [defaultOptions, setDefaultOptions] = useState<WatchlistEntryOptions>({ notifications: true, sounds: 0, showBid: true, showAsk: true });
  const onUpdateDefaultOptions = (newOptions: Partial<WatchlistEntryOptions>): void => {
    setDefaultOptions({ ...defaultOptions, ...newOptions });
  };

  const updateEntryOptions = (entry: WatchlistEntry, options: Partial<WatchlistEntryOptions>) => {
    const update = { options: { ...entry.options, ...options } };
    updateWatch(entry.id, update);
  };

  const onOpenTrade = async (data?: WatchlistEntry) => {
    if (!data) return;
    addTradeWidgetCallback(data);
  };

  const rowButtonFuncs: WatchRowButtonComponentParams = {
    onCancel,
    onOptionsUpdate: updateEntryOptions,
    onOpenTrade,
  };

  const defs: ColDef[] = [
    { field: 'symbol', headerComponent: Headers.Symbol, valueFormatter: stringFormatter },
    { field: 'tenor', headerComponent: Headers.Tenor, valueFormatter: stringFormatter },
    {
      field: 'targetPrice',
      valueFormatter: priceFormatter,
      headerComponent: Headers.TargetPrice,
      editable: true,
      cellRenderer: TargetPriceCellComponent,
      singleClickEdit: true,
    },
    // Reference price- hidden for v1
    // TODO: get the quote since the last time this was updated
    {
      field: 'currentPrice',
      headerComponent: Headers.ReferencePriceHeader,
      cellRenderer: ReferencePriceComponent,
      hide: true,
    },
    {
      field: 'currentPrice',
      headerComponent: Headers.CurrentPriceHeader,
      cellRenderer: CurrentPriceCellComponent,
    },

    { field: 'lastPrice', hide: true },
    {
      headerComponent: Headers.Buttons,
      cellRenderer: RowButtonCellComponent,
      cellRendererParams: rowButtonFuncs,
      field: 'options.notifications',
      valueFormatter: (data: any) => {
        return data.data?.options.notifications ? 'yes' : 'no';
      },
    },
    {
      field: 'targetPriceHitTime',
      hide: true,
      sort: 'desc',
      sortable: true,
      comparator: (valueA: number, valueB: number, nodeA: IRowNode<WatchlistEntry>, nodeB: IRowNode<WatchlistEntry>, isDescending: boolean) => {
        const { data: AData } = nodeA;
        const { data: BData } = nodeB;

        if (!AData || !BData) {
          return 0;
        }

        const { targetPriceHit: APriceHit, symbol: ASymbol } = AData;
        const { targetPriceHit: BPriceHit, symbol: BSymbol } = BData;

        if (APriceHit === BPriceHit) {
          // If they've both hit, or neither has hit, sort by symbol
          return ASymbol.toUpperCase() < BSymbol.toUpperCase() ? 1 : -1;
        }

        // Otherwise, put the one that has hit first
        else if (APriceHit) {
          return 1;
        }

        return -1;
      },
    },
  ];

  const containerRef = useRef<HTMLDivElement | null>(null);

  const [width, setWidth] = useState(-1);
  useEffect(() => {
    setWidth(containerRef.current?.getBoundingClientRect().width || 600);
  }, []);

  return (
    <Widget {...otherProps} id={id} ref={containerRef}>
      <Header title={otherProps.title}>
        <Settings id={`watchlist-widget-${id}`} widgetId={id} />
      </Header>

      <Grid
        gridId="watchlist-grid"
        rowData={entries as any[]}
        columnDefs={defs}
        defaultColDef={defaultColDef}
        rowHeight={32}
        headerHeight={40}
        maxHeight="100%"
        autoSizeStrategy={{
          type: 'fitProvidedWidth',
          width,
        }}
        getRowId={getRowId}
        onGridReady={onGridReady}
        suppressContextMenu={true}
        suppressRowVirtualisation={props.suppressGridVirtualisation || false}
        suppressColumnVirtualisation={props.suppressGridVirtualisation || false}
        readOnlyEdit={true}
        rowClassRules={{ 'price-hit': 'data.targetPriceHit == true' }}
      />

      <WatchlistFooter
        defaultOptions={defaultOptions}
        onUpdateDefaultOptions={onUpdateDefaultOptions}
        isPopupOpen={isPopupOpen}
        setPopupOpen={setIsPopupOpen}
      />

      {/* 
        MUI popups don't work very well with the widget layout code, so we're creating a pseudo popup 
        in an overlay. Still using popups for options, but they hide on mouse out, so less of an issue
      */}

      {/* Overlay - blends out the grid underneath. Layout is slightly involved but keeps content
          positioned between the widget header and the buttons in the footer underneath
      */}
      <div
        style={{
          width: '100%',
          height: 'calc(100% - 25px - 4rem)',
          top: '25px',
          paddingTop: '2rem',
          display: 'flex',
          background: 'rgba(16, 16, 16, 0.6)',
          placeItems: 'center',
          placeContent: 'center space-around',
          position: 'absolute',
          opacity: isPopupOpen ? 1 : 0,
          pointerEvents: isPopupOpen ? 'all' : 'none',
          transition: 'all 0.1s ease',
        }}
      >
        {/* Inner popup */}
        <div
          style={{
            width: 'calc(100% - 5em)',
            top: 'calc(25px + 2.5em)',
            left: '2.5em',
            backgroundColor: darkTheme.palette.background.lighter,
            transition: 'all 0.2s ease',
            zIndex: 3, // agGrid's 'no rows' overlay has a zIndex of 2
            borderRadius: '1em',
          }}
        >
          <ProductEntry
            onSubmit={(product: Product, tenor: ProductTenor | null, front: ProductTenor | null, back: ProductTenor | null, targetPrice: number) => {
              addNewWatch(product, tenor, front, back, targetPrice, defaultOptions);
              setIsPopupOpen(false);
            }}
            onCancel={() => {
              setIsPopupOpen(false);
            }}
            submitLabel="Add"
            hideTargetPrice={false}
          />
        </div>
      </div>
    </Widget>
  );
};
